import React from 'react';
import { Cell, Pie, PieChart as RechartsPieChart, Tooltip, ResponsiveContainer } from 'recharts';

import NoDataAvailable from '../no-data-available/NoDataAvailable';

export interface PieChartDataType {
    name: string;
    value: number;
    color: string;
}

interface CustomPieChartProperties {
    data: PieChartDataType[];
    innerRadius?: number;
    outerRadius?: number;
    totalLabel?: string;
    onCellClick?: (name: string) => void;
    height?: number | string;
    totalValue?: number;
}

/**
 * CustomPieChart - A shared donut chart component with a custom legend.
 */
function CustomPieChart({
    data,
    innerRadius,
    outerRadius,
    totalLabel,
    onCellClick,
    height,
    totalValue: customTotalValue,
}: CustomPieChartProperties) {
    const calculatedTotal = data.reduce((sum, item) => sum + item.value, 0);
    const displayTotalValue = customTotalValue !== undefined ? customTotalValue : calculatedTotal;

    const isEmpty = data.length === 0 || data.every((item) => item.value === 0);

    /**
     * Custom Tooltip component for Recharts.
     */
    function CustomTooltip({ active, payload }: any) {
        if (active && payload && payload.length > 0) {
            const { name, value, payload: dataPayload } = payload[0];
            return (
                <div
                    className="custom-pie-tooltip"
                    style={{
                        backgroundColor: '#fff',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        border: `1px solid ${dataPayload.color}`,
                    }}
                >
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: dataPayload.color }}>
                        {`${name}: ${value}`}
                    </p>
                </div>
            );
        }
        return null;
    }

    return (
        <div className="custom-pie-chart-wrap" style={{ height }}>
            <div className="pie-chart-container" style={{ width: '100%', height: '300px' }}>
                {isEmpty ? (
                    <NoDataAvailable />
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                            <Pie
                                data={data}
                                innerRadius={innerRadius}
                                outerRadius={outerRadius}
                                paddingAngle={2}
                                dataKey="value"
                                cx="50%"
                                cy="50%"
                                stroke="none"
                                isAnimationActive
                            >
                                {data.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                        onClick={() => onCellClick?.(entry.name)}
                                        style={{ outline: 'none', cursor: onCellClick ? 'pointer' : 'default' }}
                                    />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </RechartsPieChart>
                    </ResponsiveContainer>
                )}
                {(innerRadius as number) > 0 && !isEmpty && (
                    <div className="total-sessions-wrap">
                        <p>{totalLabel}</p>
                        <span>{displayTotalValue.toLocaleString()}</span>
                    </div>
                )}
            </div>

            {!isEmpty && (
                <div className="pie-chart-legend">
                    {data.map((item, index) => (
                        <div
                            key={index}
                            className="each-label"
                            onClick={() => onCellClick?.(item.name)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') onCellClick?.(item.name);
                            }}
                        >
                            <div
                                className="each-label-color"
                                style={{ '--label-color': item.color } as React.CSSProperties}
                            />
                            <p title={item.name}>{item.name}</p>
                            <div className="each-label-value">
                                <span>{item.value}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

CustomPieChart.defaultProps = {
    innerRadius: 60,
    outerRadius: 90,
    totalLabel: 'Total',
    onCellClick: undefined,
    height: 350,
    totalValue: undefined,
};

export default CustomPieChart;
